import React, { useEffect, useRef, useState } from "react";
import { Chip, IconButton, Paper, TextField, Tooltip } from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import LocalOfferIcon from "@material-ui/icons/LocalOffer";
import CheckIcon from "@material-ui/icons/Check";
import { isString } from "lodash";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const chipStyle = (tag) => ({
  background: tag.color || "#999",
  color: "#fff",
  fontWeight: 600,
  borderRadius: 12,
  fontSize: "0.7em",
  height: 22,
  letterSpacing: "0.03em",
});

const byName = (a, b) => (a.name || "").localeCompare(b.name || "", "pt-BR");

/**
 * Tags do contato (VIP, Retorno, ...). Usa o cadastro da tela de Tags, mas a
 * ligação é com o contato — não muda as tags do atendimento nem o Kanban.
 * Sem a prop `tags`, busca as tags do próprio contato.
 */
const ContactTagsEditor = ({ contactId, tags: initialTags, onChange }) => {
  const [selected, setSelected] = useState(initialTags || []);
  const [options, setOptions] = useState([]);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const isMounted = useRef(true);
  const inputValueRef = useRef("");

  useEffect(() => () => { isMounted.current = false; }, []);

  useEffect(() => {
    if (initialTags) {
      setSelected([...initialTags].sort(byName));
      return;
    }
    if (!contactId) return;
    api
      .get(`/contacts/${contactId}`)
      .then(({ data }) => {
        if (isMounted.current) setSelected([...(data.tags || [])].sort(byName));
      })
      .catch(() => {
        // tags são complementares — não bloqueia a tela
      });
  }, [contactId, initialTags]);

  const loadOptions = async () => {
    try {
      const { data } = await api.get("/tags/list");
      // tags marcadas como Kanban são etapas do atendimento, não do contato
      if (isMounted.current) setOptions((data || []).filter((t) => t.kanban !== 1));
    } catch (err) {
      toastError(err);
    }
  };

  const startEditing = () => {
    setEditing(true);
    loadOptions();
  };

  // texto digitado vira tag: reaproveita uma com o mesmo nome ou cria nova
  const resolveTyped = async (name) => {
    const typed = String(name || "").trim();
    if (!typed) return null;
    const existing = options.find((t) => t.name?.toLowerCase() === typed.toLowerCase());
    if (existing) return existing;
    try {
      const { data } = await api.post("/tags", { name: typed });
      return data;
    } catch (err) {
      toastError(err);
      return null;
    }
  };

  const handleChange = async (value) => {
    const typedText = inputValueRef.current;
    inputValueRef.current = "";
    const next = [];
    let created = false;

    for (const item of value || []) {
      let tag = item;
      if (isString(item) || !item) {
        tag = await resolveTyped(isString(item) ? item : typedText);
        created = created || Boolean(tag && !options.some((o) => o.id === tag.id));
      }
      if (tag?.id && !next.some((t) => t.id === tag.id)) next.push(tag);
    }

    if (created) loadOptions();

    const previous = selected;
    setSelected(next.sort(byName));
    setSaving(true);
    try {
      const { data } = await api.put(`/contacts/${contactId}/tags`, {
        tagIds: next.map((t) => t.id),
      });
      if (isMounted.current) {
        setSelected([...data].sort(byName));
        if (onChange) onChange(data);
      }
    } catch (err) {
      if (isMounted.current) setSelected(previous);
      toastError(err);
    } finally {
      if (isMounted.current) setSaving(false);
    }
  };

  if (!contactId) return null;

  if (!editing) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap" }}>
        {selected.map((tag) => (
          <Chip key={tag.id} size="small" label={tag.name} style={chipStyle(tag)} />
        ))}
        <Tooltip title={selected.length ? "Editar etiquetas do contato" : "Adicionar etiqueta ao contato"}>
          <IconButton size="small" onClick={startEditing}>
            <LocalOfferIcon style={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
      <Autocomplete
        multiple
        freeSolo
        size="small"
        style={{ flex: 1, minWidth: 220 }}
        options={options}
        value={selected}
        disabled={saving}
        filterSelectedOptions
        getOptionSelected={(option, value) => option.id === value.id}
        getOptionLabel={(option) => (isString(option) ? option : option?.name || "")}
        onChange={(e, v) => handleChange(v)}
        onInputChange={(e, v, reason) => {
          if (reason !== "reset") inputValueRef.current = v;
        }}
        renderTags={(value, getTagProps) =>
          value.filter(Boolean).map((option, index) => (
            <Chip
              key={option.id || index}
              size="small"
              label={option.name}
              style={chipStyle(option)}
              {...getTagProps({ index })}
            />
          ))
        }
        renderInput={(params) => (
          <TextField
            {...params}
            autoFocus
            variant="standard"
            placeholder="Adicionar etiqueta..."
          />
        )}
        PaperComponent={({ children }) => <Paper elevation={3}>{children}</Paper>}
      />
      <Tooltip title="Concluir">
        <IconButton size="small" onClick={() => setEditing(false)}>
          <CheckIcon style={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>
    </div>
  );
};

export default ContactTagsEditor;
